import { Alert, AlertTitle, Box, Stack } from "@mui/material";
import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { MooneLiteEULACheckboxLabel } from "Components/Auth/SignUp/MooneLiteEULACheckboxLabel";
import { type FormData, SignUpForm } from "Components/Auth/SignUp/SignUpForm";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  PartyType,
  SignUpRoleIntent,
  UserStatus,
} from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { useSearchParams } from "Navigation/useSearchParams";
import { resetError, resetUser, setSignInStep } from "State/Auth/AuthReducer";
import { SignInStep } from "State/Auth/Models/AuthStateModels";
import { signUpAsync } from "State/Auth/SignUp/SignUpState";
import type { RootStateType } from "State/Store";
import { Resources, useResource, useServerError } from "Translations/Resources";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { appUrl } from "Utils/UrlUtils";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Registration is Customer-first: every account starts as a Customer (NaturalPerson). A role
// invitation/registration link carries the wanted role as an intent — the account gets it later
// via the add-role flow.
const parseRoleIntent = (value: string | null): SignUpRoleIntent | null =>
  value && Object.values(SignUpRoleIntent).includes(value as SignUpRoleIntent)
    ? (value as SignUpRoleIntent)
    : null;

export const SignUpPage = () => {
  const [keyToResetForm, toggleKeyToResetForm] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useLoggedInUser();
  const { t } = useResource();

  useEffect(() => {
    dispatch(resetError());
  }, [dispatch]);

  const { translateError } = useServerError(
    Resources.SignUp.Error,
    Resources.SignUp.Error.General,
  );

  const { isLoading, errorCode } = useSelector((e: RootStateType) => e.auth);

  const [searchParams, setSearchParams] = useSearchParams();
  const affiliateCode = searchParams.get("affiliateCode");
  const email = searchParams.get("email");
  const roleIntent = parseRoleIntent(searchParams.get("roleIntent"));

  //const isPasswordless = searchParams.get("isPasswordless") === "true";
  const voucherCode = searchParams.get("voucherCode");

  useEffect(() => {
    if (voucherCode) {
      setLocalStorageItem(LocalStorageKeys.VoucherCodeSubsidy, voucherCode);

      // 👇️ delete each query param
      searchParams.delete("voucherCode");

      // 👇️ update state after
      setSearchParams(searchParams);
    }
  }, [voucherCode, searchParams, setSearchParams]);

  useEffect(() => {
    if (affiliateCode) {
      setLocalStorageItem(LocalStorageKeys.AffiliateCode, affiliateCode);
    }
  }, [affiliateCode]);

  useEffect(() => {
    // SSO buttons read the intent from storage (the OAuth roundtrip loses the query string).
    // A plain sign-up clears it — a stale intent from an abandoned role journey must not leak
    // into an unrelated Customer registration.
    if (roleIntent) {
      setLocalStorageItem(LocalStorageKeys.RoleIntent, roleIntent);
    } else {
      removeLocalStorageItem(LocalStorageKeys.RoleIntent);
    }
  }, [roleIntent]);

  const onSubmit = ({ login, password }: FormData) => {
    // Sign out a logged-in user before creating another account. An anonymous guest normally
    // converts in place — but a role-intent registration is a new business journey, so the guest
    // is discarded too (conversion would silently drop the intent).
    if (
      currentUser &&
      (currentUser.userStatus !== UserStatus.Anonymous || roleIntent)
    ) {
      dispatch(resetUser({}));
    }

    if (password) {
      dispatch(
        signUpAsync.request({
          model: {
            login,
            password,
            partyType: PartyType.NaturalPerson,
            accessRoleCode: AccessRoleCodes.Customer,
            affiliateCode,
            roleIntent,
          },
          navigate,
        }),
      );
    }
  };

  const onFormReset = () => {
    toggleKeyToResetForm(!keyToResetForm);
    dispatch(setSignInStep(SignInStep.Credentials));
  };

  return (
    <AuthPageLayout
      title={t(Resources.SignUp.Title[AccessRoleCodes.Customer])}
      key={`${keyToResetForm}`}
      isLoading={isLoading}
      pageType="sign-up"
      userType={AccessRoleCodes.Customer}
    >
      <Box mx={5}>
        <Stack gap={3}>
          {/* ZS-1359 Q1: when an existing Mo.one user clicks an affiliate link, /sign-up will
              reject them with UserExists. Surface a hint that they should sign in instead and ask
              the inviter to send them an in-app invitation. Shown only when an affiliateCode is
              present in the URL — the page is reachable directly via /sign-up too, where the
              disclaimer would just be noise. */}
          {affiliateCode && (
            <Alert severity="info" sx={{ alignItems: "flex-start" }}>
              <AlertTitle>
                {t(Resources.SignUp.AffiliateLandingDisclaimer.Title)}
              </AlertTitle>
              {t(Resources.SignUp.AffiliateLandingDisclaimer.Body)}
              <Box mt={2}>
                <ButtonLink
                  link={appUrl("sign-in")}
                  text={t(
                    Resources.SignUp.AffiliateLandingDisclaimer.SignInButton,
                  )}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Alert>
          )}
          <SignUpForm
            key={email}
            isLoading={isLoading}
            onSubmit={onSubmit}
            serverError={translateError(errorCode)}
            onReset={onFormReset}
            defaultLogin={email}
            confirmationCheckboxLabel={<MooneLiteEULACheckboxLabel />}
            //isPasswordless={isPasswordless}
          />
        </Stack>
      </Box>
    </AuthPageLayout>
  );
};

export default SignUpPage;
